import { MultiSelect, Switch } from "@mantine/core"
import { Icon, Modal, Spinner } from "@shopify/polaris"
import { SettingsMajor } from "@shopify/polaris-icons"
import React, { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
	useDeleteScriptTag,
	useUpdateFeatureSetting,
} from "../../hooks/useProtectHooks.js"
import { IPv4Regex, IPv6Regex } from "../../utils/ipRegex.js"
import { ProtectStateContext } from "../providers/ProtectStateProvider.jsx"

export const ProtectGlobalSettings = () => {
	const { protectState, setProtectState } = useContext(ProtectStateContext)
	const { t } = useTranslation()
	const deleteScriptTag = useDeleteScriptTag()
	const updateFeatureSetting = useUpdateFeatureSetting()

	const [settingsModalIsOpen, setSettingsModalIsOpen] = useState(false)
	const [isAdminWhitelisted, setIsAdminWhitelisted] = useState(false)
	const [ipWhitelist, setIpWhitelist] = useState([])
	const [ipError, setIpError] = useState(null)

	const [loadingDeleteScriptTag, setLoadingDeleteScriptTag] = useState(false)
	const [deleteScriptTagError, setDeleteScriptTagError] = useState(null)

	const addIp = (ipAddress) => {
		if (!IPv4Regex.test(ipAddress) && !IPv6Regex.test(ipAddress)) {
			setIpError(
				"Incorrect IP address format. Enter a valid IPv4 or IPv6 address.",
			)
		} else if (ipWhitelist.includes(ipAddress)) {
			setIpError("That IP address is already in the whitelist.")
		} else {
			setIpError(null)
			setIpWhitelist([...ipWhitelist, ipAddress])
		}
	}

	const _deleteScriptTag = async () => {
		setLoadingDeleteScriptTag(true)
		const { success, error } = await deleteScriptTag()
		setLoadingDeleteScriptTag(false)

		if (success) {
			window.location.reload()
		} else {
			setDeleteScriptTagError(error || "Unexpected error")
		}
	}

	const saveSettings = async () => {
		setProtectState({
			...protectState,
			globalSettings: {
				...protectState.globalSettings,
				isAdminWhitelisted,
				ipWhitelist,
			},
		})

		updateFeatureSetting({
			global_config: { isAdminWhitelisted, ipWhitelist },
		})

		setSettingsModalIsOpen(false)
	}

	useEffect(() => {
		if (
			protectState.globalSettings &&
			"isAdminWhitelisted" in protectState.globalSettings
		) {
			setIsAdminWhitelisted(
				protectState.globalSettings?.isAdminWhitelisted,
			)
		}
		if (protectState.globalSettings?.ipWhitelist?.length > 0) {
			setIpWhitelist(protectState.globalSettings.ipWhitelist)
		}
	}, [protectState])

	return (
		<div>
			<button
				className="neu-background shadow neu-button"
				style={{ gap: "0.5rem" }}
				onClick={() => setSettingsModalIsOpen(true)}
			>
				<Icon color="subdued" source={SettingsMajor} />
				<span style={{ fontSize: "0.9rem", fontWeight: "600" }}>
					{t("protect.global-settings.title")}
				</span>
			</button>

			<Modal
				title={
					<span style={{ fontSize: "1.5rem", fontWeight: "600" }}>
						{t("protect.global-settings.title")}
					</span>
				}
				open={settingsModalIsOpen}
				onClose={() => setSettingsModalIsOpen(false)}
			>
				<div style={{ padding: "2rem" }}>
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
						}}
					>
						<div style={{ maxWidth: "80%" }}>
							<h2
								style={{
									marginBottom: "1rem",
									fontSize: "1.2rem",
									fontWeight: "600",
								}}
							>
								{t("protect.global-settings.white-listing")}
							</h2>
							<Switch
								label={t(
									"protect.global-settings.disable-protect-admin-title",
								)}
								description={t(
									"protect.global-settings.disable-protect-admin-description",
								)}
								size="md"
								radius="lg"
								color="teal"
								style={{ marginBottom: "1.5rem" }}
								checked={isAdminWhitelisted}
								onChange={(event) =>
									setIsAdminWhitelisted(
										event.currentTarget.checked,
									)
								}
							/>
						</div>
					</div>

					<MultiSelect
						label={t("protect.global-settings.ip-whitelist-label")}
						description={t(
							"protect.global-settings.ip-whitelist-placeholder",
						)}
						placeholder="100.200.0.1"
						data={ipWhitelist}
						value={ipWhitelist}
						creatable
						searchable
						onChange={(change) => setIpWhitelist(change)}
						onCreate={(e) => addIp(e)}
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								addIp(e.target.value)
							}
						}}
						getCreateLabel={(query) => `+ Add ${query}`}
						radius="lg"
						error={ipError}
					/>

					<h2
						style={{
							marginTop: "2rem",
							fontSize: "1.2rem",
							fontWeight: "600",
						}}
					>
						Script
					</h2>

					<p style={{ fontSize: "0.8rem", color: "#777" }}>
						Uninstall the Kopy Protect Script Tag from your store.
					</p>

					{deleteScriptTagError ? (
						<p
							style={{
								fontSize: "0.8rem",
								color: "#f92d2d",
								marginTop: "1rem",
							}}
						>
							{deleteScriptTagError}
						</p>
					) : null}

					<button
						className="border shadow"
						style={{
							marginTop: "1rem",
							cursor: "pointer",
							background: "#f92d2d",
							borderRadius: "1rem",
							padding: "0.5rem 1rem",
						}}
						onClick={_deleteScriptTag}
					>
						{loadingDeleteScriptTag ? (
							<Spinner size="small" />
						) : (
							<span
								style={{
									fontSize: "0.85rem",
									fontWeight: "700",
									color: "#fff",
								}}
							>
								Uninstall
							</span>
						)}
					</button>

					<button
						className="neu-background shadow neu-button"
						style={{
							padding: "0.6rem 1.2rem",
							marginLeft: "auto",
							marginTop: "3rem",
						}}
						onClick={saveSettings}
					>
						<span style={{ fontWeight: "600" }}>
							{t("common.save")}
						</span>
					</button>
				</div>
			</Modal>
		</div>
	)
}
